import { Tooltip } from '@mui/material';
import {
    FieldNamesMarkedBoolean,
    FieldValues,
    UseFormGetValues,
} from 'react-hook-form';
import * as yup from 'yup';
import { SINGLE_NAVIGATION_PATHS } from '../../../constants/single-navigation-paths';
import { StringSchema } from 'yup';

interface TruncateWithTooltipProps {
    text: string | string[];
}

const tooltipStyles = { overflow: 'hidden', textOverflow: 'ellipsis' };

export const capitalizeFirstLetter = (inputString: string): string => {
    // Split the string into words using spaces
    const words = inputString.split(' ');

    // Capitalize the first letter of each word
    const capitalizedWords = words.map((word) => {
        // Handle empty strings or strings with no alphabetic characters
        if (word === '' || !/[a-zA-Z]/.test(word)) {
            return word;
        }
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });

    // Join the capitalized words back into a single string
    return capitalizedWords.join(' ');
};
export function transformDataToTitleCase<T>(
    data: T[],
    checkKeys: string[]
): T[] {
    return data.map((item) => {
        const transformedItem: Partial<T> = { ...item };
        for (const key in item) {
            if (Object.hasOwn(item as any, key) && checkKeys.includes(key)) {
                transformedItem[key] = capitalizeFirstLetter(
                    item[key] as string
                ) as T[Extract<keyof T, string>];
            }
        }
        return transformedItem as T;
    });
}

export const TruncateWithTooltip = ({
    text,
}: TruncateWithTooltipProps): JSX.Element => {
    const displayText = typeof text === 'string' ? text : text?.join(', ');
    return (
        <Tooltip title={displayText} arrow>
            <div style={tooltipStyles}>{displayText}</div>
        </Tooltip>
    );
};

export const isFormDirtyAndModified = (
    dirtyFields: Partial<Readonly<FieldNamesMarkedBoolean<FieldValues>>>,
    defaultVals: Record<string, any>,
    getValues: UseFormGetValues<any>
): boolean => {
    if (Object.keys(dirtyFields).length === 0) return false;
    return Object.keys(dirtyFields).some((fieldName: string) => {
        const defaultValue =
            defaultVals[fieldName] === undefined ? '' : defaultVals[fieldName];
        const currentValue = getValues(fieldName);
        return currentValue !== defaultValue;
    });
};

export const commonShowAlert = (
    showAlertFunc: any,
    hideAlertFunc: any,
    severity: 'success' | 'info' | 'warning' | 'error',
    message: string,
    details?: string,
    showAlertDetailsFunc?: any
): void => {
    showAlertFunc({
        severity: severity,
        variant: 'filled',
        closable: true,
        onClose: hideAlertFunc,
        children: message,
        details: details,
        onDetailsClicked: showAlertDetailsFunc,
    });
};

export const baseUrlParser = (url?: string): string => {
    if (!url) return '';

    const validAPIUrl: boolean =
        (url.startsWith('https://') || url.startsWith('http://localhost')) &&
        url.endsWith('/api');

    /**
     * Valid Base API URL formats:
     *
     * DEV: https://dev.xxxxx/api
     * STAGE: https://stage.xxxxx/api
     * UAT: https://uat.xxxxx/api
     * PROD: https://api.xxxxx/api
     *
     * If url is a valid api url, we return the base by removing the last '/api' part.
     * Otherwise, return empty string if is not valid.
     */
    return validAPIUrl ? url.slice(0, -4) : '';
};

/**
 * Returns a string representation of the current value showing only the first 4 significant digits.
 * @param value
 */
const getFormattedStringValue = (value: number): string => {
    return Number.parseFloat(value.toPrecision(4)).toString();
};

/**
 * This function formats any given number and adds a suffix based
 * on the corresponding order of magnitude. The symbol representation
 * should be as follows:
 *
 * K = 1,000
 * M = 1,000,000
 * G = 1,000,000,000
 * T = 1,000,000,000,000
 * P = 1,000,000,000,000,000
 *
 * @param value the current number that needs to be formatted
 * @param symbols The unit suffix that will be added to the formatted number.
 */
const getFormattedValues = (value: number, symbols: string[]): string => {
    // get the current tier in the symbol array. This should be 0 to n-1.
    const tier = Math.trunc(value === 0 ? 0 : Math.log10(Math.abs(value)) / 3);

    // return the number without suffix
    if (tier === 0) return getFormattedStringValue(value);

    // get suffix and determine scale
    const suffix = symbols[tier];
    const scale = Math.pow(10, tier * 3);

    // scale the number
    const scaled = value / scale;

    // format number and add suffix
    return getFormattedStringValue(scaled) + ' ' + suffix;
};

export const formattedMetricValues = (value: number): string => {
    const metricUnits = ['', 'K', 'M', 'G', 'T', 'P'];
    return getFormattedValues(value, metricUnits);
};

export const formattedByteValues = (bytes: number): string => {
    const byteUnits = ['', 'KB', 'MB', 'GB', 'TB', 'PB'];
    return getFormattedValues(bytes, byteUnits);
};

export const possiblyNavigateToMetadataSearch = (
    navigate: Function,
    defaultPath: string,
    searchParams: URLSearchParams
): void => {
    /**
     * Check whether we landed in the current component from the metadata search via searchParams.
     * If we got here via metadata search, make sure we send the user back
     * to the previous metadata search results screen. Otherwise, navigate the user to the default path.
     */
    const metadataSearchPage = searchParams.get(
        SINGLE_NAVIGATION_PATHS.METADATA_SEARCH
    );

    navigate(metadataSearchPage ?? defaultPath);
};

export const noSpaceAllowedInSchemaTest = (
    customSchema: StringSchema,
    message: string
): StringSchema => {
    return customSchema.test('no-spaces', message, (value) => {
        return value ? value.trim().length > 0 : true;
    });
};

export const numericSchemaTest = (
    message: string,
    regex: RegExp,
    optional: boolean = false
): any => {
    return yup
        .mixed()
        .optional()
        .test('type', message, (value: any) => {
            if (optional && !value) return true;

            return !Number.isNaN(value);
        })
        .test('valid-numbers', message, (value: any) => {
            if (optional && !value) return true;

            return regex.test(value);
        });
};

export const removeCommentsFromQuery = (query: string): string => {
    const regex = /(\/\*[^*]*\*\/)|(\/\/[^*]*)/g;
    return query.replaceAll(regex, '');
};

export const isUnsupportedQuery = (
    query: string,
    unsupportedKeywords: string[]
): boolean => {
    const escapedKeywords = unsupportedKeywords.map((keyword) =>
        keyword.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`)
    );
    const pattern = new RegExp(`\\b(${escapedKeywords.join('|')})\\b`, 'i');
    return pattern.test(removeCommentsFromQuery(query));
};
